/* @jsx h */
import { defineComponent, h, onMounted, Ref, ref, computed } from "vue";
import { FoodType, Order, CartItem, CheckoutData } from "@lemmonhq/types";
import {
  calculateOrderItemTotal,
  calculateOrderTotal,
  formatPrice,
  getOrderDisplayName,
  createCartItem,
  generateCartItemId,
} from "@lemmonhq/orders";
import { api } from "@app/data/api";
import { OrderCardMain } from "../Orders";
import { getOrderFoodTypeStatusColor } from "@app/utils/utils";
import { useShop } from "../../providers";
import { useAuth } from "@app/vue/providers/Auth";
import { Loading } from "../UIComponents";
import { useLocalization } from "@lemmonhq/uikit";
import { ConfirmReduceQuantitiesModal } from "@app/vue/pages/Tables/payments/ConfirmReduceQuantitiesModal";
//api.createOrder
interface TableOrdersProps {
  tableId: number;
  order: Order;
  orders: Ref<Order[]>;
  foodTypes: FoodType[];
}

export const TableOrdersMain = defineComponent<TableOrdersProps>({
  name: "TableOrdersMain",
  props: ["tableId", "order", "foodTypes", "onStatusChange", "closeOpenOrder"],
  setup(props) {
    const auth = useAuth();
    const formatTime = (date: string) => {
      return new Date(date).toLocaleTimeString("fr-CH", {
        hour: "numeric",
        minute: "numeric",
        hour12: false,
      });
    };

    return () => {
      const currency = auth.user.value?.restaurant.currency || "CHF";
      return (
        <div class="space-y-4 ">
          {props.orders.value
            .sort((orderA, orderB) => {
              const a = new Date(orderA.created_at);
              const b = new Date(orderB.created_at);
              return a.getTime() - b.getTime();
            })
            .map((order) => (
              <div key={order.id} class=" rounded-lg overflow-hidden border ">
                <div class="py-4 px-2 flex items-start justify-between border-b ">
                  <div class="flex items-center justify-center gap-4">
                    <div class="flex flex-col">
                      <span class="text-sm  flex gap-1 items-center">
                        Order {getOrderDisplayName(order)} |
                        <div class=" font-light">
                          {formatTime(order.created_at)}
                        </div>
                      </span>
                      <div class="flex items-center gap-2 mt-1">
                        <div
                          class="w-2 h-2 rounded-full"
                          style={getOrderFoodTypeStatusColor(order.status)}
                        />
                        <span class="text-sm capitalize">{order.status}</span>
                      </div>
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-sm ">
                      {formatPrice(
                        calculateOrderTotal(order),
                        true,
                        2,
                        currency,
                      )}
                    </div>
                  </div>
                </div>

                <div class="divide-y divide-base-200 ">
                  {(order.items || []).map((item) => (
                    <div key={item.id} class="p-2  ">
                      <div class="flex items-start justify-center">
                        <div class="flex gap-2 items-center justify-center flex-1">
                          <div class="w-4 h-4 bg-gray-700/50 rounded flex items-center justify-center text-sm">
                            {item.quantity}
                          </div>
                          <div class="flex-1">
                            <div class="text-sm font-medium">
                              {item.product?.name}
                            </div>
                            {item.notes && (
                              <div class="text-sm italic  mt-1">
                                Note: {item.notes}
                              </div>
                            )}
                            {item.bundles && item.bundles.length > 0 && (
                              <div class="mt-2 space-y-1">
                                {item.bundles.map((bundle, idx) => (
                                  <div
                                    key={idx}
                                    class="text-sm text-accent/50 "
                                  >
                                    <span class="w-1 h-1 rounded-full bg-gray-400" />
                                    <span>{bundle.extra?.title}</span>
                                    {bundle.price > 0 && (
                                      <span class="">
                                        (+
                                        {formatPrice(
                                          bundle.price.toString(),
                                          true,
                                          2,
                                          currency,
                                        )}
                                        )
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div class="text-xs font-medium">
                          {formatPrice(
                            calculateOrderItemTotal(item),
                            true,
                            2,
                            currency,
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      );
    };
  },
});

export const TableOrders = defineComponent<{
  tableId: number;
  reservationId?: string | null;
  /** When set, only that order of the table is rendered. */
  orderId?: number;
}>({
  name: "TableOrders",
  props: ["tableId", "reservationId", "orderId", "onStatusChange"],
  setup(props) {
    const { foodTypes } = useShop();
    const { t } = useLocalization();
    const loading = ref(false);
    const orders = ref<Order[]>([]);
    const quantityChanges = ref<Record<number, Record<number, number>>>({});
    const quantityMode = ref<"add" | "reduce" | null>(null);
    const showChanges = ref(false);
    const sendingOrder = ref(false);
    const showReduceConfirmModal = ref(false);

    const fetchData = async () => {
      loading.value = true;
      try {
        const [fetchedOrders, fetchedTables] = await Promise.all([
          api.getTableOrders(props.tableId),
          api.getTablesMeta(),
        ]);
        orders.value = fetchedOrders;
      } catch (error) {
        console.error("Failed to fetch orders in Orders Page", error);
      } finally {
        loading.value = false;
      }
    };
    onMounted(() => {
      fetchData();
    });
    const shop = useShop();

    // `orders` stays the full table list so lookups and mutations keep working;
    // only what we render is narrowed when a single order was asked for.
    const visibleOrders = computed(() =>
      props.orderId
        ? orders.value.filter((order) => order.id === props.orderId)
        : orders.value,
    );

    const onStatusChange = async (
      orderId: number,
      foodTypeId: number,
      currentStatus: string,
    ) => {
      try {
        const resp = await api.changeFoodTypeStatus({
          type: "food_type",
          orderID: orderId,
          foodTypeId: foodTypeId as number,
          currentStatus,
        });
        console.log("handleStatusChange", resp);
        const { orders: updatedOrders, kitchenTickets } = resp;

        // Update orders ref with new data
        orders.value = orders.value.map((order) => {
          const match = updatedOrders.find((el) => el.id === order.id);
          return match ? match : order;
        });
        if (kitchenTickets && kitchenTickets.length > 0) {
          //
          try {
            Promise.all(
              kitchenTickets
                .filter((el) => el.doc.type === "kitchenTicket")
                .map(async (job) => shop.print(job)),
            );
          } catch (e) {
            console.log("error printing ticket", e);
          }
        }
      } catch (error) {
        console.error("Failed to update order status:", error);
      }
    };

    const handleItemQuantityChange = (
      orderId: number,
      itemId: number,
      newQuantity: number,
    ) => {
      const order = orders.value.find(
        (currentOrder) => currentOrder.id === orderId,
      );
      const originalItem = order?.items?.find((item) => item.id === itemId);
      if (!originalItem) return;

      const originalQuantity = originalItem.quantity;
      if (newQuantity < 0) return;

      const nextMode =
        newQuantity > originalQuantity
          ? "add"
          : newQuantity < originalQuantity
            ? "reduce"
            : null;

      if (nextMode && quantityMode.value && quantityMode.value !== nextMode) {
        return;
      }

      if (!quantityChanges.value[orderId]) {
        quantityChanges.value[orderId] = {};
      }

      if (newQuantity === originalQuantity) {
        delete quantityChanges.value[orderId][itemId];
        if (Object.keys(quantityChanges.value[orderId]).length === 0) {
          delete quantityChanges.value[orderId];
        }
      } else {
        quantityChanges.value[orderId][itemId] = newQuantity;
      }

      quantityMode.value =
        nextMode ||
        (Object.keys(quantityChanges.value).length > 0
          ? quantityMode.value
          : null);

      if (Object.keys(quantityChanges.value).length === 0) {
        quantityMode.value = null;
      }

      // Trigger reactivity
      quantityChanges.value = { ...quantityChanges.value };
    };

    const resetQuantityChanges = () => {
      quantityChanges.value = {};
      quantityMode.value = null;
      showChanges.value = false;
    };

    const hasModifications = computed(() => {
      return Object.keys(quantityChanges.value).length > 0;
    });

    const quantityChangeDetails = computed(() => {
      return Object.entries(quantityChanges.value).flatMap(
        ([orderId, items]) => {
          const order = orders.value.find(
            (currentOrder) => currentOrder.id === parseInt(orderId),
          );
          if (!order) return [];

          return Object.entries(items).flatMap(([itemId, newQuantity]) => {
            const originalItem = order.items?.find(
              (item) => item.id === parseInt(itemId),
            );
            if (!originalItem || !originalItem.product) return [];

            const delta = newQuantity - originalItem.quantity;
            if (delta === 0) return [];

            return [
              {
                orderId: order.id,
                orderName: getOrderDisplayName(order),
                itemId: originalItem.id,
                itemName: originalItem.product.name,
                originalQuantity: originalItem.quantity,
                newQuantity,
                delta,
              },
            ];
          });
        },
      );
    });

    const getAdditions = (): CartItem[] => {
      const additions: CartItem[] = [];

      Object.entries(quantityChanges.value).forEach(([orderId, items]) => {
        const order = orders.value.find((o) => o.id === parseInt(orderId));
        if (!order) return;

        Object.entries(items).forEach(([itemId, newQuantity]) => {
          const originalItem = order.items?.find(
            (item) => item.id === parseInt(itemId),
          );
          if (!originalItem || !originalItem.product) return;

          const quantityDiff = newQuantity - originalItem.quantity;
          if (quantityDiff > 0) {
            // Create a CartItem for the additional quantity
            const cartItemId = generateCartItemId(
              originalItem.product.id,
              undefined,
              undefined,
              undefined,
            );
            const cartItem = createCartItem(originalItem.product, {
              quantity: quantityDiff,
              cartItemId,
            });
            additions.push(cartItem);
          }
        });
      });

      return additions;
    };

    const reducedItems = computed(() =>
      quantityChangeDetails.value
        .filter((item) => item.delta < 0)
        .map((item) => ({
          id: item.itemId,
          quantity: Math.abs(item.delta),
          name: item.itemName,
        })),
    );

    const handleSendOrder = async () => {
      if (!quantityMode.value) return;

      if (quantityMode.value === "reduce") {
        if (reducedItems.value.length === 0) return;
        showReduceConfirmModal.value = true;
        return;
      }

      sendingOrder.value = true;
      try {
        if (quantityMode.value === "add") {
          const additions = getAdditions();
          if (additions.length === 0) return;

          const checkoutData: CheckoutData = {
            serviceMethod: "dine-in",
            payload: {
              type: "cart" as const,
              object: {
                items: additions,
                tableId: props.tableId,
                reservationId: props.reservationId ?? null,
                customers: [],
                checkoutIds: [],
                serviceMethod: "dine-in",
              },
            },
          };

          await api.createOrder(checkoutData, shop);
        }

        // Clear modifications and refresh orders
        resetQuantityChanges();
        await fetchData();
      } catch (error) {
        console.error("Failed to send order:", error);
      } finally {
        sendingOrder.value = false;
      }
    };

    const handleReducedItems = async () => {
      showReduceConfirmModal.value = false;
      resetQuantityChanges();
      await fetchData();
    };

    return () => {
      if (loading.value) {
        return (
          <div class="flex justify-center items-center h-full py-32">
            <Loading />
          </div>
        );
      }

      if (!visibleOrders.value.length) {
        return (
          <div class="flex flex-col text-center justify-center items-center flex-1 gap-2 max-w-72 mx-auto h-full">
            <h2 class="text-2xl font-bold text-accent">
              {t("pos.tables.no_orders")}
            </h2>
            <h3 class="text-xl">{t("pos.tables.add_items_to_cart")}</h3>
          </div>
        );
      }

      return (
        <div class="relative flex flex-col flex-1 overflow-hidden">
          {showReduceConfirmModal.value && (
            <ConfirmReduceQuantitiesModal
              items={reducedItems.value}
              tableId={props.tableId}
              onSuccess={handleReducedItems}
              onClose={() => {
                showReduceConfirmModal.value = false;
              }}
            />
          )}
          <div
            class={[
              "relative flex flex-col flex-1 overflow-y-auto",
              // Room for the floating "send order" bar, which only exists once
              // something was modified.
              hasModifications.value ? "pb-[200px]" : "",
            ]}
          >
            <div class="space-y-4">
              {[...visibleOrders.value]
                .sort((orderA, orderB) => {
                  const a = new Date(orderA.created_at);
                  const b = new Date(orderB.created_at);
                  return a.getTime() - b.getTime();
                })
                .map((order) => (
                  <div key={order.id} class="text-xs">
                    <OrderCardMain
                      orders={orders}
                      order={order}
                      foodTypes={foodTypes.value}
                      onFoodStatusChange={onStatusChange}
                      onOrderCancel={() => {
                        //
                      }}
                      onOrderUpdate={() => {
                        //
                      }}
                      closeOpenOrder={async () => {
                        return false;
                      }}
                      allowAdditions={true}
                      quantityMode={quantityMode.value}
                      onItemQuantityChange={handleItemQuantityChange}
                      modifiedQuantities={quantityChanges.value[order.id]}
                    />
                  </div>
                ))}
            </div>
          </div>
          {hasModifications.value && (
            <div class="absolute bottom-0 left-1/2 -translate-x-1/2 w-[100%] mb-4 pt-2 px-2">
              <div class="rounded-xl border border-base-300 bg-base-100 shadow-lg p-2.5 space-y-2">
                <div class="flex items-start justify-between gap-2">
                  <div>
                    <div class="flex items-center gap-2">
                      <span
                        class={`badge badge-xs ${
                          quantityMode.value === "reduce"
                            ? "badge-warning"
                            : "badge-primary"
                        }`}
                      >
                        {quantityMode.value === "reduce"
                          ? t("table_orders.main.reduce")
                          : t("general.words.add")}
                      </span>
                      <span class="text-xs text-base-content/70">
                        {quantityChangeDetails.value.length}{" "}
                        {quantityChangeDetails.value.length === 1
                          ? t("table_orders.main.change")
                          : t("table_orders.main.changes")}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        showChanges.value = !showChanges.value;
                      }}
                      class="text-xs text-base-content/60 underline underline-offset-2 mt-1"
                    >
                      {showChanges.value
                        ? t("table_orders.main.hide_changes")
                        : t("table_orders.main.show_changes")}
                    </button>
                  </div>
                  <div class="flex items-center gap-1">
                    <button
                      onClick={resetQuantityChanges}
                      type="button"
                      class="btn btn-ghost btn-xs px-2"
                      disabled={sendingOrder.value}
                    >
                      {t("general.words.reset")}
                    </button>
                  </div>
                </div>

                {showChanges.value && (
                  <div class="space-y-1 max-h-28 overflow-y-auto text-[11px]">
                    {quantityChangeDetails.value.map((item) => (
                      <div
                        key={`${item.orderId}-${item.itemId}`}
                        class="flex items-center justify-between gap-2 rounded-md bg-base-200/70 px-2 py-1"
                      >
                        <div class="min-w-0">
                          <div class="font-medium truncate">
                            {item.itemName}
                          </div>
                          <div class="text-base-content/50 truncate">
                            {t("general.words.order")} {item.orderName}
                          </div>
                        </div>
                        <div class="text-right whitespace-nowrap">
                          <div
                            class={
                              item.delta > 0 ? "text-success" : "text-warning"
                            }
                          >
                            {item.originalQuantity} → {item.newQuantity}
                          </div>
                          <div class="text-base-content/50">
                            {item.delta > 0 ? "+" : ""}
                            {item.delta}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={handleSendOrder}
                  disabled={sendingOrder.value}
                  class="w-full bg-primary hover:bg-primary-focus text-primary-content font-bold py-2 px-3 rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingOrder.value ? (
                    <>
                      <span class="material-symbols-outlined animate-spin">
                        refresh
                      </span>
                      {quantityMode.value === "reduce"
                        ? t("table_orders.main.reducing")
                        : t("table_orders.main.sending")}
                    </>
                  ) : (
                    <>
                      <span class="material-symbols-outlined">
                        {quantityMode.value === "reduce" ? "remove" : "send"}
                      </span>
                      {quantityMode.value === "reduce"
                        ? t("table_orders.main.reduce_items")
                        : t("table_orders.main.send_order")}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      );
    };
  },
});
